import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const ActivateId = () => {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [sponsorName, setSponsorName] = useState("");
  const [error, setError] = useState("");
  const [userId, setUserId] = useState("");
  const [fetchingSponsor, setFetchingSponsor] = useState(false);
  const [userDetails, setUserDetails] = useState(null); // Store user details for stake API

  const currentUserId = localStorage.getItem("UserId");
  const BOT_AMOUNT = 70;

  // Fetch user wallet balance
  const fetchBalance = async () => {
    try {
      setBalanceLoading(true);
      const res = await axios.post(`${API_BASE}/walletbalance`, {
        UserId: currentUserId,
      });
      const balanceData = res.data.recordset ? res.data.recordset[0] : res.data;
      setBalance(balanceData.upgradebal || 0);
    } catch (err) {
      console.error("Wallet Balance Error:", err);
      setBalance(0);
      toast.error("Failed to load wallet balance");
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (currentUserId) {
      fetchBalance();
      setUserId(currentUserId);
    }
  }, []);

  // Fetch sponsor/user info
  const fetchUserInfo = async (userIdToCheck) => {
    if (!userIdToCheck || userIdToCheck === currentUserId) {
      setSponsorName("");
      setError("");
      setUserDetails(null);
      return;
    }

    try {
      setFetchingSponsor(true);
      const res = await axios.post(`${API_BASE}/sponsorinfo`, { sponsorId: userIdToCheck });
      if (res.data.success) {
        setSponsorName(res.data.sponsorName);
        setError("");
        // Store user details for the stake API
        setUserDetails({
          userId: userIdToCheck,
          name: res.data.sponsorName,
          sponsorId: res.data.sponsorId || null
        });
      } else {
        setSponsorName("");
        setError("User ID not found");
        setUserDetails(null);
      }
    } catch (err) {
      console.error("User Info Error:", err);
      setSponsorName("");
      setError("Error fetching User ID info");
      setUserDetails(null);
    } finally {
      setFetchingSponsor(false);
    }
  };

  const handleUserIdChange = (e) => {
    const value = e.target.value;
    setUserId(value);
    if (value.trim() !== "") {
      fetchUserInfo(value);
    } else {
      setSponsorName("");
      setError("");
      setUserDetails(null);
    }
  };

  // Update the handleActivate function in your ActivateId component
const handleActivate = async () => {
  if (!userId || userId.trim() === "") {
    toast.warning("Please enter a User ID to activate");
    return;
  }

  if (error) {
    toast.error("Please enter a valid User ID");
    return;
  }
  
  if (BOT_AMOUNT > balance) {
    toast.error(`Insufficient balance! Available: $${balance.toFixed(2)}`);
    return;
  }

  setLoading(true);
  
  try {
    // First, check if target user already has an active bot
    const checkResponse = await axios.post(`${API_BASE}/can-purchase-bot`, {
      Userid: userId
    });

    if (!checkResponse.data.canPurchase) {
      toast.error(checkResponse.data.message);
      setLoading(false);
      return;
    }

    // Proceed with bot activation
    const response = await axios.post(`${API_BASE}/activate-bot`, {
      CurrentUserid: currentUserId,
      TargetUserid: userId,
      Amount: BOT_AMOUNT,
      PackageId: 1, // 1 for 1-year subscription
    });

    if (response.data.success) {
      toast.success(response.data.message);
      setUserId("");
      setSponsorName("");
      await fetchBalance(); // Refresh balance
      
      // Optional: Show expiry date
      if (response.data.data && response.data.data.expiryDate) {
        toast.info(`Bot active until: ${new Date(response.data.data.expiryDate).toLocaleDateString()}`);
      }
    } else {
      toast.error(response.data.error || "Activation failed");
    }
  } catch (err) {
    console.error("Activation Error:", err);
    
    if (err.response) {
      const errorMessage = err.response.data?.error || "Transaction failed!";
      toast.error(errorMessage);
      
      if (err.response.status === 409) {
        // Bot already active
        toast.warning("User already has an active bot subscription!");
      } else if (err.response.status === 401) {
        toast.error("Invalid User ID. Please login again.");
      } else if (err.response.status === 404) {
        toast.error("Target user not found.");
      }
    } else if (err.request) {
      toast.error("Network error. Please check your connection.");
    } else {
      toast.error("An unexpected error occurred. Please try again.");
    }
  } finally {
    setLoading(false);
  }
};

  return (
    <Layout>
      <ToastContainer 
        position="top-center" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="activate-id-page">
        <div className="page-header">
          <h1>Activate Trading</h1>
          <p>Activate your trading for 1 year</p>
        </div>

        {/* Balance Card */}
        <div className="balance-card-activate">
          <h3>Available Balance</h3>
          <div className="balance-amount">
            {balanceLoading ? (
              <div className="loading-text">Loading...</div>
            ) : (
              <>${balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
            )}
          </div>
          <p>Activation Fee: ${BOT_AMOUNT} (1 Year Subscription)</p>
        </div>

        {/* Single Package Box */}
        <div className="single-package-box">
          <div className="package-icon">
            <i className="fa fa-rocket"></i>
          </div>
          <h2>Activation Package</h2>
          <div className="package-price">${BOT_AMOUNT}</div>
          <div className="package-duration">
            <i className="fa fa-calendar"></i>
            <span>1 Year Subscription</span>
          </div>
          <div className="package-features">
            <div className="feature">
              <i className="fa fa-check-circle"></i>
              <span>24/7 Automated Trading</span>
            </div>
            <div className="feature">
              <i className="fa fa-check-circle"></i>
              <span>AI-Powered Strategies</span>
            </div>
            <div className="feature">
              <i className="fa fa-check-circle"></i>
              <span>Real-time Market Analysis</span>
            </div>
            <div className="feature">
              <i className="fa fa-check-circle"></i>
              <span>Priority Support</span>
            </div>
          </div>
        </div>

        {/* User ID Input */}
        <div className="user-id-section">
          <div className="form-group">
            <label>User ID to Activate</label>
            <div className="input-wrapper">
              <i className="fa fa-user"></i>
              <input
                type="text"
                placeholder="Enter team member's User ID"
                value={userId}
                onChange={handleUserIdChange}
                disabled={loading}
              />
            </div>
            {fetchingSponsor ? (
              <div className="sponsor-loading">
                <i className="fa fa-spinner fa-spin"></i> Verifying User ID...
              </div>
            ) : sponsorName ? (
              <div className="sponsor-success">
                <i className="fa fa-check-circle"></i> User: {sponsorName}
              </div>
            ) : error ? (
              <div className="sponsor-error">
                <i className="fa fa-exclamation-circle"></i> {error}
              </div>
            ) : null}
          </div>
        </div>

        {/* Activation Summary */}
        <div className="activation-summary">
          <h3>Activation Summary</h3>
          <div className="summary-details">
            <div className="summary-item">
              <span>Amount:</span>
              <strong>${BOT_AMOUNT}</strong>
            </div>
            <div className="summary-item">
              <span>Duration:</span>
              <strong>1 Year</strong>
            </div>
            <div className="summary-item">
              <span>User ID:</span>
              <strong>{userId || 'Not selected'}</strong>
            </div>
            <div className="summary-item">
              <span>Available Balance:</span>
              <strong className={BOT_AMOUNT > balance ? 'insufficient' : ''}>
                ${balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </strong>
            </div>
          </div>
          <button 
            className="btn-primary activate-btn" 
            onClick={handleActivate}
            disabled={loading || !userId || !!error || (BOT_AMOUNT > balance)}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin"></i> ACTIVATING...
              </>
            ) : (
              <>
                <i className="fa fa-rocket"></i> ACTIVATE NOW
              </>
            )}
          </button>
        </div>

        {/* Insufficient Balance Warning */}
        {BOT_AMOUNT > balance && !balanceLoading && (
          <div className="insufficient-warning">
            <i className="fa fa-exclamation-triangle"></i>
            Insufficient balance! Please add funds to continue.
          </div>
        )}

        {/* Additional Info */}
        <div className="activation-info">
          <p>
            <i className="fa fa-info-circle"></i>
            Note: This activation will deduct ${BOT_AMOUNT} from your wallet balance 
            and activate the trading bot for the specified user ID for 1 year.
          </p>
        </div>
      </div>

      </Layout>
  );
};

export default ActivateId;